import type { ReactNode } from 'react';
import {
  createElement,
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import type {
  InfiniteScrollOptions,
  ScrollToIndexOptions,
} from '@components/virtual-list';

import type {
  ContextPanelOpenOptions,
  ContextPanelScope,
} from './context-panel-context';
import { useContextPanel } from './context-panel-context';

// Rows remaining from the loaded tail at which stepping fetches the next page.
const DEFAULT_PREFETCH_THRESHOLD = 3;

interface RowDerivation<TRow> {
  index: number;
  isFilteredOut: boolean;
  nextKey: string | null;
  prevKey: string | null;
  row: TRow;
  rowKey: string;
}

/**
 * Locates a row in the current dataset and derives what the panel renders from.
 * Takes the row explicitly rather than reading state, so a derivation can be
 * made for a row before it becomes the selection.
 */
const deriveRow = <TRow>(
  rows: TRow[],
  getRowKey: (row: TRow) => string,
  selectedRow: TRow,
  retained: { key: string; row: TRow } | null,
): RowDerivation<TRow> => {
  const rowKey = getRowKey(selectedRow);
  const index = rows.findIndex(candidate => getRowKey(candidate) === rowKey);
  const isFilteredOut = index === -1;

  // A filtered-out row is absent from `rows`, so fall back to the newest copy
  // seen while it was present, and only then to the row as selected.
  const retainedRow =
    retained !== null && retained.key === rowKey ? retained.row : null;

  return {
    index,
    isFilteredOut,
    nextKey:
      !isFilteredOut && index < rows.length - 1
        ? getRowKey(rows[index + 1])
        : null,
    prevKey: index > 0 ? getRowKey(rows[index - 1]) : null,
    row: isFilteredOut ? (retainedRow ?? selectedRow) : rows[index],
    rowKey,
  };
};

export interface RowDetailPanelRenderArgs<TRow> {
  /**
   * Whether a row follows the active one in the loaded data.
   */
  hasNext: boolean;
  /**
   * Whether a row precedes the active one in the loaded data.
   */
  hasPrev: boolean;
  /**
   * Whether the active row has left the dataset, e.g. excluded by a filter.
   */
  isFilteredOut: boolean;
  /**
   * Closes the panel and clears the active row.
   */
  onClose: () => void;
  /**
   * Steps to the following row, absent when there is none.
   */
  onNext?: () => void;
  /**
   * Steps to the preceding row, absent when there is none.
   */
  onPrev?: () => void;
  /**
   * The active row, kept current with `data` and holding its last known values
   * once it is filtered out.
   */
  row: TRow;
  /**
   * The active row's `getRowKey` result.
   */
  rowKey: string;
}

export interface UseRowDetailPanelOptions<TRow> {
  /**
   * The loaded rows, in display order.
   */
  data: TRow[];
  /**
   * Stable identity for a row; also keys the panel body.
   */
  getRowKey: (row: TRow) => string;
  /**
   * Paging state; stepping near the loaded tail fetches the next page from it.
   */
  infinite?: Pick<InfiniteScrollOptions, 'hasMore' | 'loadMore'>;
  /**
   * Merged into every `open()` call, e.g. `padded` and `width`.
   */
  panelOptions?: Omit<Partial<ContextPanelOpenOptions>, 'content' | 'header'>;
  /**
   * Rows from the loaded tail at which stepping fetches the next page.
   *
   * @default 3
   */
  prefetchThreshold?: number;
  /**
   * Builds the panel body and header for the active row. Re-invoked when the
   * active row, its neighbors or its filtered-out state change — so anything it
   * captures that must update independently of those belongs inside `content`.
   */
  render: (args: RowDetailPanelRenderArgs<TRow>) => {
    content: ReactNode;
    header: ReactNode;
  };
  /**
   * The panel scope to drive.
   *
   * @default 'page'
   */
  scope?: ContextPanelScope;
  /**
   * Scrolls a row index into view, whether or not it is currently rendered.
   */
  scrollToIndex?: (index: number, options?: ScrollToIndexOptions) => void;
}

export interface UseRowDetailPanelResult<TRow> {
  /**
   * The active row's key, or null when no row is selected.
   */
  activeRowKey: string | null;
  /**
   * Closes the panel and clears the active row.
   */
  close: () => void;
  /**
   * Whether the panel is currently open.
   */
  isOpen: boolean;
  /**
   * Whether a row should take the active highlight.
   */
  isRowActive: (row: TRow) => boolean;
  /**
   * Opens the panel on a row, or closes it when that row is already active.
   */
  toggleRow: (row: TRow) => void;
}

/**
 * Drives a context panel showing detail for one row of a table, including
 * stepping between rows and keeping up with a dataset that filters or pages
 * underneath it.
 */
export function useRowDetailPanel<TRow>({
  data,
  getRowKey,
  infinite,
  panelOptions,
  prefetchThreshold = DEFAULT_PREFETCH_THRESHOLD,
  render,
  scope = 'page',
  scrollToIndex,
}: UseRowDetailPanelOptions<TRow>): UseRowDetailPanelResult<TRow> {
  const { close: closePanel, isOpen, open: openPanel } = useContextPanel(scope);

  // Retains the row, not just its key, since a filtered-out row is absent from
  // `data`. Boxing it gives every activation a fresh identity, so re-selecting
  // the same row still reaches the sync effect below.
  const [selection, setSelection] = useState<{ row: TRow } | null>(null);

  const getRowKeyRef = useRef(getRowKey);
  getRowKeyRef.current = getRowKey;

  // Retains the newest in-data copy, so a row that later leaves `data` renders
  // the values it last had. Kept out of `selection`, whose identity marks a
  // fresh activation.
  const retainedRef = useRef<{ key: string; row: TRow } | null>(null);

  // Memoized because the hook subscribes to the panel context, so its own
  // `open()` re-renders it, and a windowed host can accumulate many pages.
  const derived = useMemo(
    () =>
      selection === null
        ? null
        : deriveRow(
            data,
            getRowKeyRef.current,
            selection.row,
            retainedRef.current,
          ),
    [data, selection],
  );

  const activeIndex = derived?.index ?? -1;
  const activeRow = derived?.row ?? null;
  const activeRowKey = derived?.rowKey ?? null;
  const isFilteredOut = derived?.isFilteredOut ?? false;
  const nextKey = derived?.nextKey ?? null;
  const prevKey = derived?.prevKey ?? null;

  // Read by the callbacks and the sync effect so that neither depends on values
  // whose identity changes every render, which would re-open the panel in a
  // loop. The effect depends on the derived values above instead.
  const current = {
    activeRowKey,
    data,
    getRowKey,
    infinite,
    isOpen,
    panelOptions,
    prefetchThreshold,
    render,
    scrollToIndex,
  };
  const latest = useRef(current);
  latest.current = current;

  const openedForRef = useRef<{ row: TRow } | null>(null);
  const skipNextSyncRef = useRef(false);
  const stepRef = useRef<(key: string) => void>(() => {});

  const close = useCallback(() => {
    setSelection(null);
    closePanel();
  }, [closePanel]);

  const openFor = useCallback(
    (derivation: Omit<RowDerivation<TRow>, 'index'>) => {
      const { panelOptions: options, render: renderPanel } = latest.current;
      const {
        isFilteredOut: filteredOut,
        nextKey: next,
        prevKey: prev,
        row,
        rowKey,
      } = derivation;

      const { content, header } = renderPanel({
        hasNext: next !== null,
        hasPrev: prev !== null,
        isFilteredOut: filteredOut,
        onClose: close,
        onNext: next === null ? undefined : () => stepRef.current(next),
        onPrev: prev === null ? undefined : () => stepRef.current(prev),
        row,
        rowKey,
      });

      openPanel({
        ...options,
        // Keying on the row remounts the body when stepping, so a retained query
        // result for the previous row cannot render under the new row's header.
        content: createElement(Fragment, { key: rowKey }, content),
        header,
      });
    },
    [close, openPanel],
  );

  const activate = useCallback(
    (row: TRow) => {
      const {
        data: rows,
        getRowKey: keyOf,
        infinite: paging,
        prefetchThreshold: threshold,
        scrollToIndex: scrollTo,
      } = latest.current;

      const box = { row };
      const derivation = deriveRow(rows, keyOf, row, retainedRef.current);

      setSelection(box);

      // Opened here, not left to the sync effect, so it batches into the click's
      // own commit. A second commit costs the host an extra render before paint,
      // which shows as a stutter as the panel starts animating in.
      openedForRef.current = box;
      skipNextSyncRef.current = true;
      openFor(derivation);

      if (derivation.index === -1) return;

      scrollTo?.(derivation.index, { align: 'auto', behavior: 'smooth' });

      if (paging?.hasMore && derivation.index >= rows.length - threshold) {
        paging.loadMore();
      }
    },
    [openFor],
  );

  const step = useCallback(
    (key: string) => {
      const { data: rows, getRowKey: keyOf } = latest.current;
      const row = rows.find(candidate => keyOf(candidate) === key);

      if (row !== undefined) activate(row);
    },
    [activate],
  );
  stepRef.current = step;

  const toggleRow = useCallback(
    (row: TRow) => {
      const {
        activeRowKey: openKey,
        getRowKey: keyOf,
        isOpen: panelIsOpen,
      } = latest.current;

      if (panelIsOpen && openKey === keyOf(row)) {
        close();
        return;
      }

      activate(row);
    },
    [activate, close],
  );

  const isRowActive = useCallback((row: TRow) => {
    const {
      activeRowKey: openKey,
      getRowKey: keyOf,
      isOpen: panelIsOpen,
    } = latest.current;

    return panelIsOpen && openKey !== null && keyOf(row) === openKey;
  }, []);

  // Written on commit rather than during render: a render React starts and then
  // discards must not leave behind a row the user never saw.
  useIsomorphicLayoutEffect(() => {
    if (activeRowKey === null) {
      retainedRef.current = null;
    } else if (activeIndex !== -1) {
      retainedRef.current = { key: activeRowKey, row: data[activeIndex] };
    }
  }, [activeIndex, activeRowKey, data]);

  // Keeps an already-open panel in step with a dataset that filters or pages
  // underneath it. Layout timing so a re-sync lands before the next paint.
  useIsomorphicLayoutEffect(() => {
    if (selection === null || activeRow === null || activeRowKey === null) {
      openedForRef.current = null;
      skipNextSyncRef.current = false;
      return;
    }

    // `activate` already opened this selection in the click's own commit.
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    // Re-syncing one selection must not resurrect a panel closed by something
    // other than this hook, e.g. a navigation-driven close. Clicking the row
    // again makes a new selection, which reopens it.
    if (openedForRef.current === selection && !latest.current.isOpen) return;

    openedForRef.current = selection;

    openFor({
      isFilteredOut,
      nextKey,
      prevKey,
      row: activeRow,
      rowKey: activeRowKey,
    });
  }, [
    activeRow,
    activeRowKey,
    isFilteredOut,
    nextKey,
    openFor,
    prevKey,
    selection,
  ]);

  return { activeRowKey, close, isOpen, isRowActive, toggleRow };
}

export default useRowDetailPanel;
